import { sqItemFinderStore, sqWorkbookStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { isAgentReplaceableSearch, resolveAgentSearchTarget } from '@/itemFinder/itemFinder.agentContext';
import { SearchTypeEnum } from '@/itemFinder/itemFinder.constants';
import {
  SCOPED_TO_GLOBALLY_SCOPED_NULL_VALUE,
  createDefaultStoredTableNameProperty,
  findScopedToProperty,
  isStoredTableSearch,
  searchSupportsScopedToPredicate,
} from '@/itemFinder/itemFinder.utilities';
import { EDITOR_TYPES } from '@/tableDefinitionEditor/tableDefinition.constants';
import { doTrack } from '@/track/trackProxy.service';
import { base64guid } from '@/utilities/guid.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { sqDatasourcesApi } from '@seeqdev/api/endpoint/DatasourcesApi';
import { sqHierarchiesApi } from '@seeqdev/api/endpoint/HierarchiesApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqStoredTablesApi } from '@seeqdev/api/endpoint/StoredTablesApi';
import { sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import { OperatorEnum } from '@seeqdev/api/model/PropertyFilterInputV1';
import { SeeqNames } from '@seeqdev/constants';
import type { ReactSelectOption } from '@/core/IconSelect.molecule';
import type { AIUpdatePayload } from '@/itemFinder/itemFinder.agentContext';
import type {
  AIPayload,
  HierarchySearchOptions,
  Property,
  Search,
  SearchType,
} from '@/itemFinder/itemFinder.constants';
import type { DatasourceLabel, UpdateSearchPayload } from '@/itemFinder/itemFinder.store';
import type { LoadedSearchesStorePolicy } from '@/itemFinder/itemFinder.types';
import type { Datasource } from '@/search/search.types';
import type { EditorType } from '@/tableDefinitionEditor/tableDefinition.types';
import type { TrackInformation } from '@/track/track.types';
import type { SelectOptionIF } from '@/usage/Usage.page';
import type { ItemFinderInputV1 } from '@seeqdev/api/model/ItemFinderInputV1';
import type { ItemFinderOutputV1 } from '@seeqdev/api/model/ItemFinderOutputV1';
import type { ItemPreviewV1 } from '@seeqdev/api/model/ItemPreviewV1';
import type { ItemSearchPreviewV1 } from '@seeqdev/api/model/ItemSearchPreviewV1';

const buildItemFinderTrackInformation = (itemFinder: ItemFinderInputV1): TrackInformation => {
  const trackInformation: TrackInformation = {
    scopedTo: itemFinder.scopedTo,
    name: itemFinder.name,
    searchType: getItemFinderSearchType(itemFinder),
  };

  if (itemFinder.propertySearches?.length) {
    trackInformation.propertySearches = JSON.stringify(itemFinder.propertySearches);
  }
  if (itemFinder.fixedListSearches?.length) {
    trackInformation.fixedListSearches = JSON.stringify(itemFinder.fixedListSearches);
  }
  if (itemFinder.hierarchySearches?.length) {
    trackInformation.hierarchySearches = JSON.stringify(itemFinder.hierarchySearches);
  }
  if (itemFinder.storedTableSearches?.length) {
    trackInformation.storedTableSearches = JSON.stringify(itemFinder.storedTableSearches);
  }
  if (itemFinder.scalingTableUUIDColumnSearches?.length) {
    trackInformation.scalingTableUUIDColumnSearches = JSON.stringify(itemFinder.scalingTableUUIDColumnSearches);
  }

  return trackInformation;
};

const getItemFinderSearchType = (itemFinder: ItemFinderInputV1): string[] => {
  const types: string[] = [];
  if (itemFinder.propertySearches?.length) types.push(SearchTypeEnum.PROPERTY);
  if (itemFinder.fixedListSearches?.length) types.push(SearchTypeEnum.FIXEDLIST);
  if (itemFinder.hierarchySearches?.length) types.push(SearchTypeEnum.HIERARCHY);
  if (itemFinder.storedTableSearches?.length) types.push(SearchTypeEnum.STOREDTABLE);
  if (itemFinder.scalingTableUUIDColumnSearches?.length) types.push(SearchTypeEnum.MATERIALIZEDTABLEUUIDCOLUMN);
  return types;
};

export function setName(name: string) {
  flux.dispatch('ITEM_FINDER_SET_NAME', { name });
}

export function setCronSchedule(cronSchedule: string[]) {
  flux.dispatch('ITEM_FINDER_SET_CRON_SCHEDULE', { cronSchedule });
}

export function setTimezone(timezone: string | undefined) {
  flux.dispatch('ITEM_FINDER_SET_TIMEZONE', { timezone });
}

export function addSearch() {
  flux.dispatch('ITEM_FINDER_ADD_SEARCH');
}

export function removeSearch(index: number) {
  flux.dispatch('ITEM_FINDER_REMOVE_SEARCH', index);
}

export function updateSearch(payload: UpdateSearchPayload) {
  flux.dispatch('ITEM_FINDER_UPDATE_SEARCH', payload);
}

export function ensureSearchScopedTo(search: Search, workbookId: string | undefined): void {
  if (!workbookId || !searchSupportsScopedToPredicate(search)) {
    return;
  }

  if (findScopedToProperty(search.predicates)) {
    return;
  }

  updateSearch({
    searchId: search.searchId,
    action: 'ADD_PROPERTY',
    propertyData: {
      propertyName: SeeqNames.Properties.ScopedTo,
      operator: OperatorEnum.WITHIN,
    },
    propertyValues: [SCOPED_TO_GLOBALLY_SCOPED_NULL_VALUE, workbookId],
  });
}

export function setStoredTablesAvailable(storedTablesAvailable: ItemPreviewV1[]) {
  flux.dispatch('ITEM_FINDER_SET_STORED_TABLES_AVAILABLE', { storedTablesAvailable });
}

export function clearInvalidStoredTables(storedTables: ItemSearchPreviewV1[]) {
  const searches = sqItemFinderStore.searches;
  const storedTableIds = storedTables.map((st) => st.id);
  searches
    .filter((search) => isStoredTableSearch(search) && !storedTableIds.includes(search.storedTableId))
    .forEach((search) => {
      updateSearch({
        searchId: search.searchId,
        action: 'UPDATE_SEARCH_FIELDS',
        searchData: {
          storedTableId: undefined,
          storedTableColumns: [],
        },
      });
    });
}

export async function refreshStoredTables() {
  try {
    const { data } = await sqItemsApi.searchItems({
      types: [SeeqNames.Types.StoredTable],
      scope: [sqWorkbookStore.workbookId],
      limit: 1000,
    });
    setStoredTablesAvailable(data.items ?? []);
    clearInvalidStoredTables(data.items ?? []);
  } catch (error) {
    errorToast({ httpResponseOrError: error });
  }
}

export async function updateStoredTable(storedTableId: string) {
  const searchesToUpdate = sqItemFinderStore.searches.filter(
    (search) => isStoredTableSearch(search) && search.storedTableId === storedTableId,
  );

  if (searchesToUpdate.length === 0) {
    return;
  }

  const storedTableColumns = await loadStoredTableColumns(storedTableId);

  searchesToUpdate.forEach((search) => {
    updateSearch({
      searchId: search.searchId,
      action: 'UPDATE_SEARCH_FIELDS',
      searchData: {
        storedTableId,
        storedTableColumns,
      },
    });
  });
}

export const createUpdateItemFinder = async (
  itemFinder: ItemFinderInputV1,
  itemFinderId?: string,
  editorType?: EditorType,
  trackConfigurationEvents = true,
): Promise<ItemFinderOutputV1> => {
  const { data: itemFinderOutput } = await (itemFinderId
    ? sqItemsApi.updateItemFinder(itemFinder, { id: itemFinderId })
    : sqItemsApi.createItemFinder(itemFinder));

  if (trackConfigurationEvents) {
    let trackCategory = 'Scaling Table Configuration';
    if (editorType && editorType === EDITOR_TYPES.ITEM) {
      trackCategory = 'Tree Builder';
    }
    const trackAction = itemFinderId ? 'Edit Item Finder' : 'Create Item Finder';
    const trackInformation: TrackInformation = buildItemFinderTrackInformation(itemFinder);
    doTrack(trackCategory, trackAction, trackInformation);

    if (itemFinder.cronSchedule) {
      doTrack(trackCategory, 'Set Schedule', {
        numberOfSchedules: itemFinder.cronSchedule.length,
        schedules: itemFinder.cronSchedule,
      });
    }
  }

  return itemFinderOutput;
};

export const removeDatasourceProperties = (searchId: string) => {
  const search = sqItemFinderStore.searches.find((candidate) => candidate.searchId === searchId);

  if (!search) {
    return;
  }

  const datasourceIdProperty = search.predicates?.find(
    (predicate) => predicate.propertyName === SeeqNames.Properties.DatasourceId,
  );
  const datasourceClassProperty = search.predicates?.find(
    (predicate) => predicate.propertyName === SeeqNames.Properties.DatasourceClass,
  );

  if (datasourceClassProperty) {
    updateSearch({
      searchId,
      action: 'UPDATE_PROPERTY',
      propertyId: datasourceClassProperty.propertyId,
      propertyData: {
        datasources: [],
      },
    });
  }

  if (datasourceIdProperty) {
    updateSearch({
      searchId,
      propertyId: datasourceIdProperty.propertyId,
      action: 'REMOVE_PROPERTY',
    });
  }
};

export const loadStoredTableColumns = async (storedTableId: string): Promise<ReactSelectOption[]> => {
  try {
    const { data } = await sqStoredTablesApi.getStoredTable({ id: storedTableId });

    return data.columns.map((col) => ({
      text: col.columnName,
      value: col.id,
    }));
  } catch (error) {
    errorToast({ httpResponseOrError: error });
    return [];
  }
};

export const loadHierarchySearchOptions = async (hierarchyId: string): Promise<HierarchySearchOptions> => {
  try {
    const { data: hierarchy } = await sqHierarchiesApi.getHierarchy({ id: hierarchyId });
    const { data: tableDef } = await sqTableDefinitionsApi.getTableDefinition({ id: hierarchy.tableDefinition.id });
    const extraColumns: { text: string; value: string }[] =
      !!hierarchy.aliasColumnId && tableDef.columnDefinitions.some((col) => col.id === hierarchy.aliasColumnId)
        ? [{ text: SeeqNames.Properties.Alias, value: SeeqNames.Properties.Alias }]
        : [];

    const columns = tableDef.columnDefinitions
      .filter(
        (col) =>
          col.columnName !== SeeqNames.MaterializedTables.ItemIdColumn &&
          col.columnName !== SeeqNames.MaterializedTables.DatumIdColumn &&
          col.columnType !== ColumnTypeEnum.UUID,
      )
      .map((col) => ({
        text: col.columnName,
        value: col.columnName,
      }));

    const levelViewColumns = hierarchy.viewColumns
      .filter((col) => col.groupOrder !== undefined)
      .sort((a, b) => (a.groupOrder ?? 0) - (b.groupOrder ?? 0));

    const levelColumnOptions = levelViewColumns.map((viewCol, index) => {
      const colDef = tableDef.columnDefinitions.find((col) => col.id === viewCol.columnDefinition);
      return {
        columnId: viewCol.columnDefinition,
        columnName: colDef?.columnName ?? '',
        columnType: colDef?.columnType ?? '',
        orderValue: index + 1,
      };
    });

    return {
      hierarchyTableProperties: extraColumns.concat(columns),
      hierarchyLevelColumns: levelColumnOptions,
      hierarchy: { id: hierarchy.id, name: hierarchy.name, type: hierarchy.type },
      itemTable: {
        id: tableDef.id,
        name: tableDef.name,
        type: tableDef.type,
        columnId: tableDef.columnDefinitions.find((col) => col.columnName === SeeqNames.MaterializedTables.ItemIdColumn)
          ?.id,
      },
    };
  } catch (error) {
    errorToast({ httpResponseOrError: error });
    throw error;
  }
};

export const addDatasourcePropertyAndValue = (
  searchId: string,
  propertyId: string,
  valueId: string,
  selectedDatasources: SelectOptionIF<Datasource>[],
  ancestorPredicateId?: string,
) => {
  const datasourceLabel: DatasourceLabel[] = [];
  const selectedDatasourceIds = selectedDatasources.map((datasource) => datasource.value.datasourceId as string);
  const datasourceClassValues: { value: string; propertyId: string; searchId: string; valueId: string }[] = [];
  const datasourceIdValues: { value: string; propertyId: string; searchId: string; valueId: string }[] = [];

  const search = sqItemFinderStore.searches.find((candidate) => candidate.searchId === searchId);
  const datasourceIdProperty = search?.predicates?.find(
    (predicate) => predicate.propertyName === SeeqNames.Properties.DatasourceId,
  );

  selectedDatasources.forEach((datasource) => {
    const { datasourceClass, datasourceId, name, id } = datasource.value;

    datasourceClassValues.push({
      value: datasourceClass as string,
      propertyId,
      searchId,
      valueId: base64guid(),
    });
    if (datasourceIdProperty) {
      datasourceIdValues.push({
        value: datasourceId as string,
        propertyId: datasourceIdProperty.propertyId,
        searchId,
        valueId: base64guid(),
      });
    }
    datasourceLabel.push({
      label: datasource.value.name,
      value: {
        datasourceClass: datasourceClass as string,
        datasourceId: datasourceId as string,
        name,
        id: id as string,
      },
    });
  });

  updateSearch({
    searchId,
    action: 'UPDATE_PROPERTY',
    propertyId,
    propertyData: {
      datasources: !selectedDatasources.length ? [] : datasourceLabel,
      values: !selectedDatasources.length ? [{ value: '', propertyId, searchId, valueId }] : datasourceClassValues,
      columnId: undefined,
      isColumnSelect: false,
    },
    ancestorPredicateId,
  });

  if (!datasourceIdProperty) {
    updateSearch({
      searchId,
      action: 'ADD_PROPERTY',
      propertyData: {
        propertyName: SeeqNames.Properties.DatasourceId,
        operator: OperatorEnum.WITHIN,
        isColumnSelect: false,
      },
      propertyValues: selectedDatasourceIds,
      ancestorPredicateId,
    });
  } else {
    updateSearch({
      searchId,
      action: 'UPDATE_PROPERTY',
      propertyId: datasourceIdProperty.propertyId,
      propertyData: {
        values: !selectedDatasources.length
          ? [
              {
                value: '',
                propertyId: datasourceIdProperty.propertyId,
                searchId,
                valueId: datasourceIdProperty.values[0]?.valueId,
              },
            ]
          : datasourceIdValues,
        columnId: undefined,
        isColumnSelect: false,
      },
      ancestorPredicateId,
    });
  }
};

export async function findDatasourceNamesAndIds(datasourceClass?: string, datasourceId?: string) {
  try {
    const { data } = await sqDatasourcesApi.getDatasources({ datasourceClass, datasourceId });
    if (data.datasources) {
      const match = datasourceId
        ? (data.datasources.find((datasource) => datasource.datasourceId === datasourceId) ?? data.datasources[0])
        : data.datasources[0];
      if (match) {
        return {
          name: match.name,
          id: match.id,
          datasourceId: match.datasourceId,
          datasourceClass: match.datasourceClass,
        };
      }
    }
  } catch (error) {
    errorToast({ httpResponseOrError: error });
  }
}

export const getItemFinder = async (itemFinderId: string): Promise<ItemFinderOutputV1 | undefined> => {
  try {
    const { data: itemFinderOutput } = await sqItemsApi.getItemFinder({ id: itemFinderId });
    return itemFinderOutput;
  } catch (error) {
    errorToast({ httpResponseOrError: error });
  }
};

export const addEmptyPropertyToSearch = (search: Search) => {
  if (
    search.searchType === SearchTypeEnum.PROPERTY ||
    search.searchType === SearchTypeEnum.STOREDTABLE ||
    ((search.searchType as SearchType) === SearchTypeEnum.WORKBENCH && !search.predicates)
  ) {
    updateSearch({
      searchId: search.searchId,
      action: 'ADD_PROPERTY',
      propertyData:
        search.searchType === SearchTypeEnum.STOREDTABLE
          ? createDefaultStoredTableNameProperty(String(search.searchId))
          : undefined,
    });
  }
};

export function resetItemFinder() {
  flux.dispatch('RESET_ITEM_FINDER', {});
}

export function setItemFinder({
  value: { id, name, searches, cronSchedule, timezone, loadedSearchesStorePolicy },
}: {
  value: {
    id: string;
    name: string;
    searches: Search[] | undefined;
    cronSchedule: string[] | undefined;
    timezone?: string;
    loadedSearchesStorePolicy: LoadedSearchesStorePolicy;
  };
}) {
  flux.dispatch('SET_EXISTING_ITEM_FINDER', {
    value: { id, name, searches, cronSchedule, timezone, loadedSearchesStorePolicy },
  });
}

export function setPotentialSearchesPayload(payload: AIPayload) {
  if (payload.propertySearches) {
    payload.propertySearches.forEach((propertySearch) => {
      flux.dispatch('ITEM_FINDER_ADD_PROPERTY_SEARCH', propertySearch);
    });
  }
  if (payload.fixedListSearches) {
    payload.fixedListSearches.forEach((propertySearch) => {
      flux.dispatch('ITEM_FINDER_ADD_FIXED_LIST_SEARCH', propertySearch);
    });
  }
}

/**
 * Property searches carry a hidden Scoped To predicate (see `ensureSearchScopedTo`) that the
 * form never shows the user, so the AI is never told about it (see `serializeSearchesForAgentContext`)
 * and cannot be expected to re-emit it on a full replacement. If the search being replaced had
 * one and the AI's replacement predicates don't, carry the existing one over so workbook scoping
 * isn't silently lost. That predicate is what backs the user-visible "Limit search results to those
 * scoped to this Analysis" checkbox, so dropping it would silently flip a setting the user chose.
 * Returns the original object unchanged when there's nothing to carry over. `propertySearch` is
 * assumed to have already passed `isValidAIUpdatePayload`, which is what makes the `predicates`
 * cast below safe.
 */
function carryOverScopedToPredicate(
  currentSearch: Search | undefined,
  propertySearch: Record<string, unknown>,
): Record<string, unknown> {
  const existingScopedTo = findScopedToProperty(currentSearch?.predicates);
  if (!existingScopedTo) {
    return propertySearch;
  }
  const replacementPredicates = (propertySearch.predicates as Property[] | undefined) ?? [];
  if (findScopedToProperty(replacementPredicates)) {
    return propertySearch;
  }
  return { ...propertySearch, predicates: [...replacementPredicates, existingScopedTo] };
}

/**
 * Applies an AI-requested in-place update of one existing search. Returns false when the
 * target search cannot be found or is not a type the AI may replace, so the caller can
 * surface the failure to the user. Callers must have already run `payload` through
 * `isValidAIUpdatePayload` before calling this.
 */
export function updatePotentialSearchPayload(payload: AIUpdatePayload): boolean {
  const searches = sqItemFinderStore.searches;
  const index = resolveAgentSearchTarget(searches, payload);
  if (index === undefined || !isAgentReplaceableSearch(searches[index])) {
    return false;
  }
  const search = payload.propertySearch
    ? carryOverScopedToPredicate(searches[index], payload.propertySearch)
    : payload.fixedListSearch;
  flux.dispatch('ITEM_FINDER_REPLACE_SEARCH', { index, search });
  return true;
}

export function reorderSearches(fromIndex: number, toIndex: number) {
  flux.dispatch('ITEM_FINDER_REORDER_SEARCHES', { fromIndex, toIndex });
}

export function changeSearchType(searchType: SearchType, search: Search) {
  const searchId = search.searchId;
  updateSearch({
    searchId,
    action: 'UPDATE_SEARCH_FIELDS',
    searchData: {
      searchType,
      folderId: undefined,
      types: [], // Reset to ensure conditions aren't shown if switching from folder to property
      ancestorId: undefined,
      storedTableId: undefined,
      storedTableColumns: [],
      isSimpleSearch: undefined,
      hierarchyId: undefined,
      hierarchyTableProperties: [],
      hierarchyLevelColumns: [],
      level: undefined,
      hierarchySearchMode: undefined,
      hierarchyPaths: [],
      searchItemTable: undefined,
      searchHierarchy: undefined,
      assetLeafChildFilter: undefined,
      selectedChildNames: [],
      // Default select-all to true for asset-children searches, undefined for others
      selectAllChildren: searchType === SearchTypeEnum.ASSET_LEAF_CHILDREN ? true : undefined,
    },
  });

  search.predicates?.forEach((property: { propertyId: string }) => {
    updateSearch({
      searchId,
      propertyId: property.propertyId,
      action: 'REMOVE_PROPERTY',
    });
  });

  search.ancestorPredicates?.forEach((ancestorPredicate) => {
    updateSearch({
      searchId,
      ancestorPredicateId: ancestorPredicate.ancestorPredicateId,
      action: 'REMOVE_ANCESTOR_PREDICATE',
    });
  });

  if (
    searchType === SearchTypeEnum.WORKBENCH ||
    searchType === SearchTypeEnum.PROPERTY ||
    searchType === SearchTypeEnum.HIERARCHY
  ) {
    updateSearch({
      searchId,
      action: 'ADD_PROPERTY',
    });
  }

  if (searchType === SearchTypeEnum.STOREDTABLE) {
    updateSearch({
      searchId,
      action: 'ADD_DEFAULT_STORED_TABLE_SEARCH',
    });
  }
}
